<template>
  <Component
    :is="fieldset ? 'fieldset' : 'div'"
    class="base-input"
    :class="{ 's-input--invalid': displayInvalid }"
    @keydown="handleKeydown"
  >
    <div
      v-if="label || tooltip"
      class="base-input__label-container"
      :class="{ 'base-input__label-container--small': size === 'small' }"
    >
      <SLabel
        v-if="label"
        :disabled
        :for="!fieldset ? id : undefined"
        :pt="pt?.pcLabel"
        :required
        :show-required-type
        :size
        :tag="fieldset ? 'legend' : undefined"
      >
        {{ label }}
      </SLabel>
      <STooltipInfo
        v-if="tooltip"
        :id="describedBy.ids.tooltip.value"
        class="base-input__tooltip"
        :tooltip="tooltip"
      />
    </div>
    <slot
      name="description"
      v-bind="{ description, id: describedBy.ids.description.value, size }"
    >
      <PMessage
        v-if="description"
        icon="fa-regular fa-circle-info"
        :pt="
          assign(
            { root: { id: describedBy.ids.description.value } },
            pt?.pcDescription ?? {},
          )
        "
        :size="size"
      >
        {{ description }}
      </PMessage>
    </slot>
    <div>
      <PIconField>
        <PInputIcon
          v-if="prefixIcon"
          :class="prefixIcon"
          :pt="pt?.pcPrefixIcon"
          :size
        />
        <slot
          :id="id"
          :described-by="describedBy.aria.value"
          :display-invalid="displayInvalid"
          :error-id="errorId"
          :listeners="validationListeners"
          name="input"
        />
        <PInputIcon
          v-if="suffixIcon"
          :class="suffixIcon"
          :pt="pt?.pcSuffixIcon"
          :size
        />
      </PIconField>
      <SCharacterCounter
        v-if="showCharacterCounter && !errorId"
        class="base-input__character-count"
        :disabled
        :maxlength
        :model-value="modelValueString"
        :overflow-attempted="overflowAttempted"
      />
    </div>
    <PMessage
      v-if="subtext"
      :pt="
        assign(
          { root: { id: describedBy.ids.subtext.value } },
          pt?.pcSubtext ?? {},
        )
      "
      severity="gray"
      size="small"
      variant="simple"
    >
      {{ subtext }}
    </PMessage>
    <slot
      v-if="errorId"
      :id="errorId"
      :name="errorComponent! as unknown as string"
      v-bind="errorProps"
    >
      <PMessage
        :id="errorId"
        :pt="pt?.pcError"
        :severity="errorProps.color ? toSeverity(errorProps.color) : 'error'"
        size="small"
        variant="simple"
      >
        {{ error }}
      </PMessage>
    </slot>
  </Component>
</template>

<script setup lang="ts" generic="ModelValue">
import { useTimeoutFn } from "@vueuse/core";
import { assign } from "radash";
import { computed, ref, toRef, useId } from "vue";

import { STooltipInfo } from "@solvari/common-fe";
import { useAriaIds } from "@solvari/common-fe/composables";
import { useValidationProvider } from "@solvari/common-fe/validation";

import type {
  BaseInputEmits,
  BaseInputPassThroughOptions,
  BaseInputProps,
} from "@/molecules/baseInput.ts";

import PIconField from "@/atoms/PIconField.vue";
import PInputIcon from "@/atoms/PInputIcon.vue";
import PMessage from "@/atoms/PMessage.vue";
import SCharacterCounter from "@/atoms/SCharacterCounter.vue";
import SLabel from "@/atoms/SLabel.vue";
import { toSeverity } from "@/helpers/backwardsCompatibility.ts";
import { useSuffixIcon } from "@/helpers/useSuffixIcon.ts";

const props = withDefaults(
  defineProps<
    BaseInputProps<ModelValue> & {
      characterCounter?: boolean | number | string;
      fieldset?: boolean;
      maxlength?: number;
      pt?: BaseInputPassThroughOptions;
    }
  >(),
  {
    characterCounter: false,
    showValidState: true,
    rules: () => [],
    fieldset: false,
  },
);

const emit = defineEmits<BaseInputEmits>();

const id = useId();

const describedBy = useAriaIds(
  {
    tooltip: () => props.tooltip,
    description: () => props.description,
    subtext: () => props.subtext,
  },
  () => props.ariaDescribedby,
);

const {
  validationListeners,
  displayValid,
  displayInvalid,
  error,
  errorId,
  errorProps,
  errorComponent,
  displayValidating,
} = useValidationProvider(
  toRef(() => props.modelValue),
  props,
  emit,
);

const modelValueString = computed(
  () => props.modelValue as string | null | undefined,
);

const showCharacterCounter = computed(
  () => !!props.maxlength && props.characterCounter !== false,
);

const overflowAttempted = ref(false);
const { start: startOverflowReset } = useTimeoutFn(
  () => (overflowAttempted.value = false),
  600,
  { immediate: false },
);

function handleKeydown(event: KeyboardEvent) {
  if (
    !props.maxlength ||
    !showCharacterCounter.value ||
    event.key.length !== 1 ||
    event.ctrlKey ||
    event.metaKey ||
    modelValueString.value?.length !== props.maxlength
  ) {
    return;
  }

  overflowAttempted.value = true;
  startOverflowReset();
}

const suffixIcon = useSuffixIcon({
  loading: () => props.loading,
  displayValid,
  displayInvalid,
  displayValidating,
  suffixIcon: () => props.suffixIcon,
  showValidState: () => props.showValidState,
});

// oxlint-disable-next-line typescript/no-explicit-any
defineSlots<Record<string, (...slot: any[]) => never>>();
</script>

<style lang="postcss">
.base-input {
  @apply flex flex-col gap-2;

  &__label-container {
    @apply flex;

    /* Small → 0.25rem label gap like common-fe's sm inputs, so mixed rows align. */
    &--small {
      @apply -mb-1;
    }
  }

  &__character-count {
    @apply w-full text-right;
  }

  &__tooltip {
    @apply ml-2;
  }
}
</style>
